import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { ReactComponent as AvatarUnset } from "../../Assets/icons/unsetAvatar.svg";
import { ReactComponent as CallIcon } from "../../Assets/icons/calls/callIconColor.svg";
import { ReactComponent as VideoCallIcon } from "../../Assets/icons/callControlButtons/videoIcon.svg";
import { ReactComponent as CloseIcon } from "../../Assets/icons/callControlButtons/closeCallNoBg.svg";
import { incomingCallSound } from "../../Pages/ChatPage";
import { useTranslate } from '@tolgee/react';
import { displayName } from "../../Helpers/chatHelper/NameHelper";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { checkMicrophonePermission } from "../../Helpers/RequestMichrophonePermission";
import { useNotify } from "../../Helpers/Notifications";
import { useCallsService } from "../../Context/CallsContext";
import { useWebSocketDataService } from "../../Context/WebsocketContext";
import WebSocketObserver from "../../Helpers/websocket/WebSocketObserver";
import { openCall, canOpenCall } from "../../Helpers/navigationHelper";
import { useNavigate } from "react-router";
import { useAuthService } from "../../Context/AuthContext";
import { useMeetingsService } from "../../Context/MeetingContext";

const CallModal = () => {
  const {
    showMessageDetailsModal,
    setShowMessageDetailModal,
    showGroupChatDetails,
    setShowGroupChatDetails,
    showQuickChatDetails,
    setShowQuickChatDetails,
  } = useGenericComponentsStateService();
  const { t } = useTranslate();
  const { sendCloseCallMessage } = useWebSocketDataService();
  const { user, userRef } = useAuthService();

  const {
    incomingCallRef,
    setIncomingCallDetails,
    incomingCallDetails,
    getCallDetails,
    answeredCallUUIDRef,
    activeCallRef,
    selectedCall,
    answeredCallUUID,
    handleSetAsnweredCallUUID,
    setSelectedCall
  } = useCallsService();

  const { currentMeetingRef } = useMeetingsService();

  const { add_observer, remove_observer } = useWebSocketDataService();

  const details = incomingCallDetails?.details;

  const { notifyError } = useNotify();
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const handleAnswerIncomingCall = async () => {
    handleSetAsnweredCallUUID(incomingCallRef.current ?? null);
    checkMicrophonePermission().then((permission) => {
      if (permission === "granted") {
        var openedCall = openCall(navigate, incomingCallRef.current);
        incomingCallSound.stop();

        closePopup();

        if (showMessageDetailsModal) {
          setShowMessageDetailModal(false);
        }
        if (showGroupChatDetails) {
          setShowGroupChatDetails(false);
        }
        if (showQuickChatDetails) {
          setShowQuickChatDetails(false);
        }

        if (!openedCall) {
          handleSetAsnweredCallUUID(null);
          notifyError(t('call_modal.another_call_in_progress'));
        }
      } else if (permission === "denied") {
        notifyError(t('call_modal.enable_calling_message'));
      }
    });
  };

  const closePopup = useCallback(() => {
    incomingCallRef.current = null;
    setIncomingCallDetails(null);
    handleSetAsnweredCallUUID(null); // Add this
    setShowModal(false);
  }, [setIncomingCallDetails, handleSetAsnweredCallUUID]);

  const handleCloseCallModal = useCallback(() => {
    const uuid = incomingCallRef.current;
    sendCloseCallMessage(uuid);
    incomingCallSound.stop();
    closePopup();
  }, [sendCloseCallMessage, closePopup]);

  let incomingMessage = t('call_modal.incoming_audio');
  let asnwerIcon = (
    <CallIcon
      style={{
        maxWidth: "25px",
        maxHeight: "25px",
        color: "white",
      }}
    />
  );

  if (details?.video) {
    incomingMessage = t('call_modal.incoming_video');
    asnwerIcon = (
      <VideoCallIcon
        style={{ maxWidth: "24px", maxHeight: "24px", color: "white" }}
      />
    );
  }

  const displayAvatar = (details) => {
    let display = <AvatarUnset />;

    if (details && details.created_by && details.created_by.profile_picture) {
      display = <img src={details.created_by.profile_picture} alt="avatar" />;
    }

    return display;
  };

  const handleGetIncomingCallDetails = useCallback(
    (uuid) => {
      getCallDetails(
        uuid,
        (response) => {
          setIncomingCallDetails(response.data);
          setShowModal(true);
        },
        (error) => {
          console.log(error);
          incomingCallRef.current = null;
        }
      );
    },
    [getCallDetails, setIncomingCallDetails, setShowModal, incomingCallRef]
  );

  const handleCallsWsResponse = useCallback(
    async (response) => {
      console.log("[EMOTII-DBG] CallsChannel WS message (parent window):", JSON.stringify(response), "| incomingCallRef:", incomingCallRef.current, "| activeCallRef:", activeCallRef.current?.details?.uuid ?? null, "| showModal:", showModal);
      if (response.update === "close") {
        if (
          incomingCallRef.current !== null &&
          incomingCallRef.current === response.call_uuid
        ) {
          handleCloseCallModal();
        }
      }

      if (response.update === "open") {
        const callUUID = response.call_uuid;
        const canOpen = await canOpenCall();
        if (
          !canOpen ||
          showModal ||
          incomingCallRef.current ||
          activeCallRef.current
        ) {
          return;
        } else {
          incomingCallRef.current = callUUID;
          handleGetIncomingCallDetails(callUUID);
        }
      }
      if (response.update === "participant_answered" && !activeCallRef.current) {
        getCallDetails(
          response.call_uuid,
          (response) => {
            setSelectedCall(response.data.details);
          },
          (error) => {
            console.log(error);
          }
        );
      }

      if (
        response.update === "participant_answered" ||
        response.update === "participant_denied"
      ) {
        if (
          incomingCallRef.current != null &&
          userRef.current?.id === response.participant_id &&
          incomingCallRef.current === response.call_uuid
        ) {
          closePopup();
        }
      }
    },
    [handleCloseCallModal, handleGetIncomingCallDetails, closePopup]
  );

  const CallsChannelObserver = useMemo(
    () =>
      new WebSocketObserver("CallsChannel", (message) => {
        handleCallsWsResponse(message);
      }),
    [handleCallsWsResponse]
  );

  useEffect(() => {
    add_observer(CallsChannelObserver);

    return () => {
      remove_observer(CallsChannelObserver);
      incomingCallSound.stop();
    };
  }, []);

  useEffect(() => {
    if (showModal) {
      try {
        const playPromise = incomingCallSound.play();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise.catch((err) => {
            console.warn("Failed to play incoming call sound:", err);
          });
        }
      } catch (err) {
        console.warn("Error playing incoming call sound:", err);
      }
    } else {
      try {
        incomingCallSound.stop();
      } catch (err) {
        console.warn("Error stopping incoming call sound:", err);
      }
    }
  }, [showModal]);

  return (
    <CustomDialog
      open={showModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <CustomDialogContent>
        <UserWrapper>
          <ContactAvatar>{displayAvatar(details)}</ContactAvatar>
          <CallerName>{displayName(details)}</CallerName>
        </UserWrapper>
        <Details> {incomingMessage}</Details>
      </CustomDialogContent>

      <DialogActionsCustom>
        <Button
          onClick={handleCloseCallModal}
          className="btn closeBtn"
          style={{ gap: "5px" }}
        >
          <CloseIcon
            style={{
              maxWidth: "25px",
              maxHeight: "25px",
              color: "transparent",
            }}
          />
          {t('call_modal.decline')}
        </Button>
        <Button
          className="btn answerBtn"
          onClick={handleAnswerIncomingCall}
          autoFocus
          style={{ gap: "5px" }}
        >
          {asnwerIcon}
          {t('call_modal.answer')}
        </Button>
      </DialogActionsCustom>
    </CustomDialog>
  );
};

export default CallModal;

const CustomDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 4px;
    background: ${({ theme }) => theme.menu.background};
    min-width: 350px;
    max-width: 350px;
  }

  .btn {
    font-size: 16px;
    font-weight: 500;
    border-radius: 4px;
    min-width: 150px;
    text-transform: none !important;
  }

  .closeBtn {
    color: white;
    background: ${({ theme }) => theme.menu.deleteColor};
    &:hover {
      background: red !important;
    }
  }

  .answerBtn {
    color: white;
    background: ${({ theme }) => theme.valid_green};
    &:hover {
      background: green !important;
    }
  }
`;
const CustomDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 15px;
  justify-conten: center;
  padding: 20px 20px 0 20px !important;
`;
const Details = styled.h1`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;
const CallerName = styled.h1`
  font-size: 22px;
  font-weight: 500;
  line-height: 26px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const DialogActionsCustom = styled(DialogActions)`
  display: flex;
  justify-content: center !important;
  gap: 20px;
  webkit-justify-content: center;
  padding: 20px !important;
`;

const UserWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ContactAvatar = styled.div`
  display: flex;
  height: 45px;
  width: 45px;
  max-width: 45px;
  min-height: 45px;
  min-width: 45px;
  max-height: 45px;
  overflow: hidden;
  border-radius: 100%;
  //   height: 100%;
  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  svg {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;
